// src/utils/moqUtils.js
/**
 * Normalize and validate MOQ pricing tiers
 * Ensures tiers are properly ordered and non-overlapping
 * @param {Array} tiers - Array of MOQ tier objects {minQty, maxQty, unitPrice}
 * @returns {Array} Normalized and validated tiers
 */
export const normalizeAndValidateTiers = (tiers) => {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    throw new Error('At least one MOQ tier is required');
  }

  // Sort tiers by minQty
  const sortedTiers = [...tiers].sort((a, b) => a.minQty - b.minQty);

  // Validate first tier starts at 1 or higher
  if (sortedTiers[0].minQty < 1) {
    throw new Error('First MOQ tier must have minQty of at least 1');
  }

  // Validate and normalize
  const normalizedTiers = sortedTiers.map((tier, index) => {
    if (typeof tier.minQty !== 'number' || tier.minQty < 1) {
      throw new Error(`Invalid minQty in tier ${index + 1} (must be >= 1)`);
    }
    if (typeof tier.unitPrice !== 'number' || tier.unitPrice <= 0) {
      throw new Error(`Invalid unitPrice in tier ${index + 1}`);
    }

    // Handle maxQty
    let maxQty = tier.maxQty;
    if (index === sortedTiers.length - 1) {
      maxQty = 999999; // 마지막 tier는 무한대
    } else if (typeof maxQty !== 'number' || maxQty < tier.minQty) {
      maxQty = sortedTiers[index + 1].minQty - 1; // 다음 tier 기준 자동 계산
    }

    return {
      minQty: Math.floor(tier.minQty),
      maxQty: Math.floor(maxQty),
      unitPrice: parseFloat(tier.unitPrice.toFixed(2))
    };
  });

  // Validate and fix gaps/overlaps instead of throwing error
  for (let i = 1; i < normalizedTiers.length; i++) {
    const prev = normalizedTiers[i - 1];
    const current = normalizedTiers[i];
    
    // If there's a gap, adjust the current tier's minQty
    if (current.minQty > prev.maxQty + 1) {
      console.warn(`MOQ tier gap detected, adjusting minQty from ${current.minQty} to ${prev.maxQty + 1}`);
      current.minQty = prev.maxQty + 1;
    }
    
    // If there's an overlap, adjust the previous tier's maxQty
    if (current.minQty <= prev.maxQty) {
      console.warn(`MOQ tier overlap detected, adjusting maxQty from ${prev.maxQty} to ${current.minQty - 1}`);
      prev.maxQty = current.minQty - 1;
    }
  }

  return normalizedTiers;
};

/**
 * Get unit price for a given quantity from normalized MOQ tiers
 */
export const getUnitPriceFromTiers = (normalizedTiers, quantity) => {
  if (!Array.isArray(normalizedTiers) || normalizedTiers.length === 0) {
    throw new Error('Invalid MOQ tiers');
  }

  if (typeof quantity !== 'number' || quantity < 1) {
    throw new Error('Invalid quantity');
  }

  const tier = normalizedTiers.find(t => quantity >= t.minQty && quantity <= t.maxQty);
  return tier ? tier.unitPrice : normalizedTiers[normalizedTiers.length - 1].unitPrice;
};

/**
 * Calculate total price for a quantity using MOQ tiers
 */
export const calculateTotalPrice = (normalizedTiers, quantity) => {
  const unitPrice = getUnitPriceFromTiers(normalizedTiers, quantity);
  return parseFloat((unitPrice * quantity).toFixed(2));
};